import { ORDERED_PARTS_ENDPOINT, PART_STATUS_COLOR_MAP } from "../../../../consts";
import { useCRUD } from "../../../../api/crud";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import { useTranslation } from "react-i18next";
import { useHasPermission } from "../../../../contexts/auth";
import { formatDate, formatMoney, formatQuantity } from "../../../../utils";
import { useBreadcrumbs } from "../../../../components/ui/breadcrumps/breadcrumbsContext";
import {
  DetailHeader,
  DetailMetadata,
  DetailPage,
  DetailRow,
  DetailSection,
  UserDisplay,
} from "../../../../components/ui/detailView";
import { StatusChip } from "../../../../components/ui/statusChip/statusChip.component";

type OrderedPartBasicInfoProps = {
  previewId?: string | number;
};

export const OrderedPartBasicInfo = ({ previewId }: OrderedPartBasicInfoProps = {}) => {
  const { id, orderedPartId } = useParams();
  const { t } = useTranslation();
  const { get } = useCRUD(ORDERED_PARTS_ENDPOINT);
  const { setOverride } = useBreadcrumbs();
  const [orderedPart, setOrderedPart] = useState<any | null>(null);
  const canViewOrderedParts = useHasPermission("service.view_orderedpart");
  const entityId = previewId ?? orderedPartId;

  useEffect(() => {
    const fetchOrderedPart = async () => {
      if (entityId) {
        const response = await get(entityId);
        if (response.data) {
          setOrderedPart(response.data);
          // Breadcrumbs reflect the real routed page only - skip when rendering as a preview.
          if (!previewId && id) {
            setOverride(`/service/stock-status/${id}/ordered-parts/${entityId}`, response.data.number || entityId);
          }
        }
      }
    };

    const handleOrderedPartUpdated = () => {
      fetchOrderedPart();
    };

    window.addEventListener("orderedPartUpdated", handleOrderedPartUpdated);
    fetchOrderedPart();
    return () => {
      window.removeEventListener("orderedPartUpdated", handleOrderedPartUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  if (!canViewOrderedParts) {
    return null;
  }

  if (!orderedPart) return <DetailPage loading />;

  const statusChip = orderedPart.status_display && (
    <StatusChip
      label={orderedPart.status_display}
      value={orderedPart.status}
      colorMap={PART_STATUS_COLOR_MAP}
    />
  );

  return (
    <DetailPage>
      <DetailHeader
        icon={<LocalShippingOutlinedIcon />}
        title={orderedPart.number || id}
        subtitle={orderedPart.part_name}
        chips={statusChip}
      />

      <DetailSection title={t("Basic Information")}>
        <DetailRow label={t("Number")} value={orderedPart.number} />
        <DetailRow label={t("Status")} value={statusChip} />
        <DetailRow label={t("Part")} value={orderedPart.part_name} />
        <DetailRow
          label={t("Availability")}
          value={`${formatQuantity(orderedPart.availability ?? 0)} / ${formatQuantity(orderedPart.quantity ?? 0)}`}
        />
        <DetailRow
          label={t("Price Net")}
          value={`${formatMoney(orderedPart.price_net ?? 0)} (+${formatQuantity(orderedPart.vat ?? 0)}% ${t("VAT")})`}
        />
        <DetailRow label={t("Total Cost")} value={formatMoney(orderedPart.total ?? 0)} />
        {orderedPart.delivered_at && (
          <DetailRow label={t("Delivered At")} value={formatDate(orderedPart.delivered_at)} />
        )}
        <DetailRow label={t("Ordered By")}>
          <UserDisplay user={orderedPart.ordered_by} />
        </DetailRow>
      </DetailSection>

      <DetailMetadata createdAt={orderedPart.created_at} updatedAt={orderedPart.updated_at} />
    </DetailPage>
  );
};
